import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { Module } from '../Module';
import { ServerService } from './ServerService';
import { SiteUrlNameCapturingRegex } from './SiteUrlMatcherFactory';
import { StateWrapperService } from './StateWrapperService';

export class StartPageService {

  public static $inject = ['$location', 'ServerService', 'StateWrapperService'];

  constructor(
    private $location: ng.ILocationService,
    private server: ServerService,
    private stateWrapperService: StateWrapperService
  ) {}

  // Returns the default server start page when none has been set.
  public getServerDefaultStartPage(): string {
    return '/';
  }

  // Given a startPageUrl and a siteUrlName, returns the navigable start page. i.e.:
  //  1. If start page url is invalid, returns default start page for current site
  //  2. Prepends site prefix to the startPageUrl if site prefix is needed (not needed for default site
  //     urls and for non-site urls)
  public getStartPageForSite(startPageUrl: string, siteUrlName: string): string {
    const startPageState: ng.ui.IState = this.getStartPageState(startPageUrl);
    const isDefaultSite = !siteUrlName;
    if (!startPageState) {
      return this.getDefaultStartPageForSite(siteUrlName);
    } else if (this.isStartPageInASite(startPageState) && !isDefaultSite) {
      return '/site/' + siteUrlName + startPageUrl;
    } else {
      return startPageUrl;
    }
  }

  // Gets a navigable start page url for the current user on the given site
  public getCurrentUserStartPageForSite(siteUrlName: string): string {
    return this.getStartPageForSite(this.server.getStartPage(), siteUrlName);
  }

  // Gets the navigable start page url for the current user on the current site
  public getCurrentUserStartPageForCurrentSite(): string {
    return this.getStartPageForSite(this.server.getStartPage(), this.server.sessionSiteUrlName());
  }

  // Get the current $location with the site prefix removed
  public getCurrentUrlAsStartPage(): string {
    return this.$location.url().replace(SiteUrlNameCapturingRegex, '');
  }

  // Get's state that matches the given startPageUrl, or a falsy value if no match
  private getStartPageState(savedStartPageUrl: string): ng.ui.IState {
    if (!savedStartPageUrl) {
      return null;
    }
    const split: string[] = savedStartPageUrl.split('?');
    const path = split[0];

    // tl;dr: Assuming no query parameters shouldn't cause any bugs right now, but it might cause weird ones in the future
    // Long version: Given our current state configuration, we only need the path to resolve the correct state.
    // I think it's possible this changes in the future. For example we could have two states that share a path
    // but take different query parameters. We should still be able to ignore the query params in this case because
    // all we're trying to do is tell whether it's a site url or not, and if they share a path they must be
    // in the same site.
    const search = {};
    return this.stateWrapperService.getMatchingState(path, search);
  }

  private isStartPageInASite(startPageState: ng.ui.IState): boolean {
    return this.stateWrapperService.isDescendantOf(startPageState, 'main.site');
  }

  private getDefaultStartPageForSite(siteUrlName: string): string {
    const siteUrls = new SpacesSiteUrls({ siteUrlName });
    return siteUrls.contentRootPath().replace(/^#/, '');
  }

}

Module.instance().service('StartPageService', StartPageService);
